/**
 * Handlers for the voice workspace `ui` frames — `open_workspace`,
 * `workspace_update`, `present_info`. Split from `applyUiAction` so the
 * dispatcher stays a thin switch; each handler owns the capability sub-gate
 * (the workspace feature flag), payload mapping/validation, and the store
 * mutation. Frames that fail any of those return `false` and are dropped —
 * a stray frame can never set state for a disabled or absent feature.
 */
import { logger } from '@heidi/runtime/logger';

import { isVoiceWorkspaceEnabled } from '../../../flags';
import { useVoiceAssistantStore } from '../store/useVoiceAssistantStore';
import { findLiveDraft } from '../workspace/draftStack';
import {
  mapOpenWorkspaceData,
  mapWorkspaceUpdateData,
  type VoiceUiOpenWorkspaceData,
  type VoiceUiWorkspaceUpdateData,
} from './mapOpenWorkspace';
import { mapPresentInfoData, type VoiceUiPresentInfoData } from './mapPresentInfo';

export const applyOpenWorkspaceFrame = (data: VoiceUiOpenWorkspaceData): boolean => {
  // Honour only when the workspace flag is on. The overlay is also
  // mount-gated in GlobalVoiceAssistant — this is the imperative belt.
  if (!isVoiceWorkspaceEnabled()) {
    logger.info('voice: ui.open_workspace ignored — workspace flag off');
    return false;
  }
  const init = mapOpenWorkspaceData(data);
  if (!init) {
    logger.warn('voice: ui.open_workspace could not map payload');
    return false;
  }
  useVoiceAssistantStore.getState().openWorkspace(init);
  return true;
};

export const applyWorkspaceUpdateFrame = (data: VoiceUiWorkspaceUpdateData): boolean => {
  if (!isVoiceWorkspaceEnabled()) {
    logger.info('voice: ui.workspace_update ignored — workspace flag off');
    return false;
  }
  const store = useVoiceAssistantStore.getState();
  if (!findLiveDraft(store.drafts)) {
    logger.warn('voice: ui.workspace_update dropped — no draft open');
    return false;
  }
  const update = mapWorkspaceUpdateData(data);
  if (!update) {
    logger.warn('voice: ui.workspace_update could not map payload');
    return false;
  }
  // Order matters: structure (steps) before content, status last so a
  // single frame can e.g. finish the step list and flip to `ready`.
  if (update.steps) store.setWorkspaceSteps(update.steps);
  if (update.step) store.updateWorkspaceStep(update.step.id, update.step.status);
  if (update.content !== undefined) store.setWorkspaceContent(update.content);
  if (update.contentDelta) store.appendWorkspaceContent(update.contentDelta);
  if (update.status) store.setWorkspaceStatus(update.status);
  if (update.error !== undefined) store.setWorkspaceCreateError(update.error);
  return true;
};

export const applyPresentInfoFrame = (data: VoiceUiPresentInfoData): boolean => {
  if (!isVoiceWorkspaceEnabled()) {
    logger.info('voice: ui.present_info ignored — workspace flag off');
    return false;
  }
  const card = mapPresentInfoData(data);
  if (!card) {
    logger.warn('voice: ui.present_info could not map payload');
    return false;
  }
  useVoiceAssistantStore.getState().presentInfoCard(card);
  return true;
};
